/**
 * Portfolio API client methods.
 *
 * Handles CSV upload, position retrieval, and trade retrieval.
 */

import { API_CONFIG } from '../config/api';
import { getToken } from './tokenStorage';
import { logApiRequest, logApiResponse, logApiError } from '../utils/errorHandler';

/**
 * Custom error class that preserves API error response data
 */
export class ApiError extends Error {
  response?: {
    data?: any;
    status?: number;
  };

  constructor(message: string, response?: { data?: any; status?: number }) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
  }
}

export interface Position {
  id: number;
  user_id: number;
  symbol: string;
  currency: string;
  quantity: number;
  cost_price: number;
  close_price: number | null;
  value: number;
  instrument_id: number | null;
  instrument_name: string | null;
  instrument_name_sch: string | null; // Simplified Chinese name
  instrument_name_tch: string | null; // Traditional Chinese name
  instrument_exchange: string | null; // Exchange code (US, HK, LSE, etc.)
  instrument_asset_type: string | null; // From instrument table
  instrument_sector: string | null; // From instrument table
  instrument_industry: string | null; // From instrument table
  asset_type: string | null; // User override, takes precedence over instrument_asset_type
  sector: string | null; // User override, takes precedence over instrument_sector
  industry: string | null; // User override, takes precedence over instrument_industry
  /**
   * CASH rows only: the annual MARGIN (borrow) rate for this balance's currency,
   * as a fraction (0.065 = 6.5% p.a.). Reference only — nothing accrues it.
   */
  cash_interest_rate: number | null;
  has_comments: boolean;
  comment_count: number;
  created_at: string | null;
  updated_at: string | null;
  // Computed properties (not stored in DB)
  cost_basis: number | null;
  unrealized_pl: number | null;
  unrealized_pl_percent: number | null;
  realized_pl: number | null;
}

export interface Trade {
  id: number;
  user_id: number;
  position_id: number | null;
  symbol: string;
  trade_datetime: string;
  quantity: number;
  trade_price: number;
  proceeds: number;
  basis: number;
  realized_pl: number;
  mtm_pl: number;
  code: string | null;
  asset_category: string;
  currency: string;
  instrument_id: number | null;
  created_at: string;
  updated_at: string;
  transaction_type: string;
  comment: string | null;
  has_comments: boolean;
  comment_count: number;
}

export interface SectorBreakdown {
  sector: string;
  value: number;
  percentage: number;
  position_count: number;
  /**
   * The currency `value` is stated in: the user's base currency, which is what the
   * API converted to. Optional only because older cached responses predate it.
   */
  currency?: string;
}

export interface GeographicBreakdown {
  country: string;
  value: number;
  percentage: number;
  position_count: number;
  currency: string;
}

/**
 * One name counted into a theme, with the amount it contributes.
 *
 * `position_count` on the row is the length of this list, so a reader who expands
 * a row can check the total they were shown rather than trusting it.
 */
export interface ThemeBreakdownConstituent {
  /**
   * The position this value came from. The stable identity for a row: `symbol` is
   * the instrument's canonical symbol and instruments are only unique on
   * `(symbol, currency)`, so two positions can share one. `null` on an older API.
   */
  position_id?: number | null;
  /** The app's own symbol for the position, e.g. `9961.HK`. */
  symbol: string;
  /** The instrument's name where the position carries one. */
  name: string | null;
  /** Market value in the row's currency — the whole position, in every theme it holds. */
  value: number;
}

/**
 * One theme's share of the book, in one of the two bases (r4 C17).
 *
 * `exposure` counts a position in every theme it holds, so it deliberately has no
 * `percentage` — the same dollars express several theses and the column can total
 * well over 100%. `primary` counts each position once, in its reviewed primary, so
 * it carries the percentage that totals 100% of the classified part.
 */
export interface ThemeBreakdownRow {
  theme_slug: string;
  value: number;
  percentage?: number;
  position_count: number;
  /** Absent only on an older API; the row is then not expandable. */
  constituents?: ThemeBreakdownConstituent[];
}

/**
 * Both theme views plus the basis each one is stated in.
 *
 * The two lists answer different questions and must not be rendered as if they
 * were one ranking: `exposure` answers "how much am I in this theme", `primary`
 * answers "how is the book split".
 *
 * The basis arrives twice, on purpose. `basis_*` is the server's own sentence and
 * `basis_*_code` names *which* basis it is; the client may translate the code but
 * never chooses the basis, and a code it does not know falls back to the server's
 * sentence rather than to nothing. Sending only the prose would leave the claim
 * untranslatable; sending only the code would let a client that has not learned a
 * new one render nothing at all.
 *
 * `tags_available` is False when the wiki registry could not be read. That is not
 * "you are in no themes": a client that conflates the two shows an empty portfolio
 * to someone whose wiki was merely restarting.
 */
export interface ThemeBreakdown {
  tags_available: boolean;
  basis_exposure: string;
  basis_exposure_code?: string;
  basis_primary: string;
  basis_primary_code?: string;
  exposure: ThemeBreakdownRow[];
  primary: ThemeBreakdownRow[];
  unclassified_value: number;
  unclassified_positions: number;
  /** The names in the unthemed bucket — the complement of every row above. */
  unclassified_holdings?: ThemeBreakdownConstituent[];
  total_value: number;
  currency: string;
}

export interface PortfolioSummary {
  total_value: number;
  total_cost_basis: number;
  total_unrealized_pl: number;
  total_unrealized_pl_percentage: number;
  position_count: number;
  sectors: string[];
  countries: string[];
}

export interface CurrencyBreakdown {
  currency: string;
  value: number;
  value_base_currency: number;
  percentage: number;
  position_count: number;
}

export interface PortfolioTotal {
  base_currency: string;
  currency_breakdown: CurrencyBreakdown[];
  total_value: number;
  total_value_base_currency: number;
}

export interface UploadResponse {
  message: string;
  positions_created: number;
  trades_created: number;
  trades_updated: number;
  positions: Position[];
  trades: Trade[];
}

/**
 * Upload CSV file to import portfolio data.
 *
 * @param file - CSV file to upload
 * @param signal - Optional AbortSignal to cancel the upload
 */
export async function uploadCSV(file: File, signal?: AbortSignal): Promise<UploadResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/upload`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      const error = new Error('Only .csv files are allowed');
      logApiError(method, url, error);
      throw error;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      const error = new Error('File size exceeds 10MB limit');
      logApiError(method, url, error);
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);

    logApiRequest(method, url, { fileName: file.name, fileSize: file.size });

    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal, // Add abort signal support
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }));
      const error = new Error(errorData.detail || 'Upload failed');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;

    // Handle AbortError specifically
    if (error instanceof Error && error.name === 'AbortError') {
      logApiError(method, url, error, duration);
      throw error; // Re-throw AbortError for caller to handle
    }

    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Get user's current portfolio positions.
 */
export async function getPositions(): Promise<Position[]> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/positions`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get positions' }));
      const error = new Error(errorData.detail || 'Failed to get positions');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Get a specific position by ID.
 */
export async function getPosition(positionId: number): Promise<Position> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/positions/${positionId}`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get position' }));
      const error = new Error(errorData.detail || 'Failed to get position');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Get user's trades with optional filtering.
 * Legacy endpoint - returns simple array.
 */
export async function getTrades(symbol?: string, limit: number = 100): Promise<Trade[]> {
  const startTime = Date.now();
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      const url = `${API_CONFIG.baseURL}/api/v1/portfolio/trades`;
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    const url = new URL(`${API_CONFIG.baseURL}/api/v1/portfolio/trades`);
    if (symbol) {
      url.searchParams.append('symbol', symbol);
    }
    url.searchParams.append('limit', limit.toString());

    const urlStr = url.toString();
    logApiRequest(method, urlStr, { symbol, limit });

    const response = await fetch(urlStr, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get trades' }));
      const error = new Error(errorData.detail || 'Failed to get trades');
      logApiError(method, urlStr, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, urlStr, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, 'portfolio/trades', error, duration);
    throw error;
  }
}

/**
 * Paginated trades response interface.
 */
export interface TradesPaginatedResponse {
  trades: Trade[];
  total_count: number;
  has_more: boolean;
  offset: number;
  limit: number;
}

/**
 * Get paginated user's trades.
 * Uses /api/v1/trades endpoint with offset/limit pagination.
 */
export async function getTradesPaginated(
  offset: number = 0,
  limit: number = 20
): Promise<TradesPaginatedResponse> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/trades?offset=${offset}&limit=${limit}`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url, { offset, limit });

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to get trades' }));
      const error = new Error(errorData.detail || 'Failed to get trades');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Get sector breakdown for user's portfolio.
 */
export async function getSectorBreakdown(
  includeUnmatched: boolean = false
): Promise<SectorBreakdown[]> {
  const startTime = Date.now();
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      const url = `${API_CONFIG.baseURL}/api/v1/portfolio/breakdown/sector`;
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    const url = new URL(`${API_CONFIG.baseURL}/api/v1/portfolio/breakdown/sector`);
    if (includeUnmatched) {
      url.searchParams.append('include_unmatched', 'true');
    }

    const urlStr = url.toString();
    logApiRequest(method, urlStr, { includeUnmatched });

    const response = await fetch(urlStr, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to get sector breakdown' }));
      const error = new Error(errorData.detail || 'Failed to get sector breakdown');
      logApiError(method, urlStr, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, urlStr, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, 'portfolio/breakdown/sector', error, duration);
    throw error;
  }
}

/**
 * Get geographic breakdown for user's portfolio.
 */
export async function getGeographicBreakdown(
  includeUnmatched: boolean = false
): Promise<GeographicBreakdown[]> {
  const startTime = Date.now();
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      const url = `${API_CONFIG.baseURL}/api/v1/portfolio/breakdown/region`;
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    const url = new URL(`${API_CONFIG.baseURL}/api/v1/portfolio/breakdown/region`);
    if (includeUnmatched) {
      url.searchParams.append('include_unmatched', 'true');
    }

    const urlStr = url.toString();
    logApiRequest(method, urlStr, { includeUnmatched });

    const response = await fetch(urlStr, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to get geographic breakdown' }));
      const error = new Error(errorData.detail || 'Failed to get geographic breakdown');
      logApiError(method, urlStr, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, urlStr, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, 'portfolio/breakdown/region', error, duration);
    throw error;
  }
}

/**
 * Get the two theme views over the live registry for the user's portfolio (r4 C17).
 */
export async function getThemeBreakdown(): Promise<ThemeBreakdown> {
  const startTime = Date.now();
  const method = 'GET';
  const urlStr = `${API_CONFIG.baseURL}/api/v1/portfolio/breakdown/theme`;

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, urlStr, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, urlStr, {});

    const response = await fetch(urlStr, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to get theme breakdown' }));
      const error = new Error(errorData.detail || 'Failed to get theme breakdown');
      logApiError(method, urlStr, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, urlStr, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, 'portfolio/breakdown/theme', error, duration);
    throw error;
  }
}

/**
 * Get portfolio summary.
 */
export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/summary`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to get portfolio summary' }));
      const error = new Error(errorData.detail || 'Failed to get portfolio summary');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Delete a position.
 *
 * T027 [US1]: Backend endpoint for deleting positions
 */
export async function deletePosition(positionId: number): Promise<void> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/positions/${positionId}`;
  const method = 'DELETE';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to delete position' }));
      const error = new ApiError(
        errorData.detail?.message || errorData.detail || 'Failed to delete position',
        { data: errorData, status: response.status }
      );
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return;
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Create a new position.
 *
 * @param params - Position creation parameters
 */
export async function createPosition(params: {
  symbol: string;
  quantity: number;
  cost_price: number;
  currency: string;
  asset_type?: string;
  merge_with_existing?: boolean;
  sector?: string;
  industry?: string;
}): Promise<Position> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/positions`;
  const method = 'POST';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    const body = {
      symbol: params.symbol,
      quantity: params.quantity,
      cost_price: params.cost_price,
      currency: params.currency,
      asset_type: params.asset_type,
      merge_with_existing: params.merge_with_existing || false,
      sector: params.sector,
      industry: params.industry,
    };

    logApiRequest(method, url, body);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to create position' }));
      const error = new ApiError(
        errorData.detail?.message || errorData.detail || 'Failed to create position',
        { data: errorData, status: response.status }
      );
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Update an existing position.
 *
 * @param positionId - Position ID to update
 * @param params - Position update parameters
 */
export async function updatePosition(
  positionId: number,
  params: {
    quantity?: number;
    cost_price?: number;
    close_price?: number | null;
    currency?: string;
    asset_type?: string;
    sector?: string;
    industry?: string;
  }
): Promise<Position> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/positions/${positionId}`;
  const method = 'PUT';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    // Only include fields that are provided
    const body: any = {};
    if (params.quantity !== undefined) body.quantity = params.quantity;
    if (params.cost_price !== undefined) body.cost_price = params.cost_price;
    if (params.close_price !== undefined) body.close_price = params.close_price;
    if (params.currency !== undefined) body.currency = params.currency;
    if (params.asset_type !== undefined) body.asset_type = params.asset_type;
    if (params.sector !== undefined) body.sector = params.sector;
    if (params.industry !== undefined) body.industry = params.industry;

    logApiRequest(method, url, body);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to update position' }));
      const error = new ApiError(
        errorData.detail?.message || errorData.detail || 'Failed to update position',
        { data: errorData, status: response.status }
      );
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}

/**
 * Get portfolio total with currency breakdown.
 */
export async function getPortfolioTotal(): Promise<PortfolioTotal> {
  const startTime = Date.now();
  const url = `${API_CONFIG.baseURL}/api/v1/portfolio/total`;
  const method = 'GET';

  try {
    const token = await getToken();

    if (!token) {
      logApiError(method, url, new Error('Authentication required'));
      throw new Error('Authentication required');
    }

    logApiRequest(method, url);

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to get portfolio total' }));
      const error = new Error(errorData.detail || 'Failed to get portfolio total');
      logApiError(method, url, { ...errorData, status: response.status }, duration);
      throw error;
    }

    logApiResponse(method, url, response.status, duration);
    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;
    logApiError(method, url, error, duration);
    throw error;
  }
}
